import { memo } from 'react';

import { TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { WorkspaceSidebarCanvasesList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCanvasesList';
import { WorkspaceSidebarCanvasesListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCanvasesListItemSkeleton';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { WorkspaceSidebarSectionSpacer } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionSpacer';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';

const CANVASES_LIMIT = 3;

export const WorkspaceSidebarRecentCanvases = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: canvases, isLoading } = useListCanvases({
    view: 'recent',
    limit: CANVASES_LIMIT,
    workspaceId: currentWorkspace?.id,
  });
  const { selectedCanvasId, selectCanvas } = useSelectedCanvasId();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);

  if (canvases.length === 0 && !isLoading) {
    return null;
  }

  return (
    <>
      <WorkspaceSidebarSection className="contents" data-testid="WORKSPACE_SIDEBAR_RECENT_CANVASES">
        <WorkspaceSidebarSectionHeader>
          <Button
            appearance="ghost"
            className="text-grey-9 hover:bg-grey-5"
            color="grey"
            onClick={toggleCollapsed}
            size="small"
            suffix={<TriangleDown className={cn('size-4 transition-transform', collapsed && '-rotate-90')} />}
          >
            Recent Canvases
          </Button>
        </WorkspaceSidebarSectionHeader>
        {!collapsed && isLoading && (
          <>
            <WorkspaceSidebarCanvasesListItemSkeleton />
            <WorkspaceSidebarCanvasesListItemSkeleton />
          </>
        )}
        {!collapsed && !isLoading && (
          <WorkspaceSidebarCanvasesList
            canvases={canvases.slice(0, CANVASES_LIMIT)}
            selectedCanvasId={selectedCanvasId}
            onSelect={selectCanvas}
          />
        )}
      </WorkspaceSidebarSection>
      <WorkspaceSidebarSectionSpacer />
    </>
  );
});

WorkspaceSidebarRecentCanvases.displayName = 'WorkspaceSidebarRecentCanvases';
